import React, { useState, useEffect, useCallback } from 'react';
import {
  Palette, Bell, MapPin, Shield, Mail,
  Save, Lock, Eye, EyeOff, Key, Zap, X, Upload
} from 'lucide-react';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { getFirebaseAuth } from '../../firebase/config';
import { sendTestEmail } from '../../services/backendActions';
import {
  fetchPlatformSettings,
  savePlatformSettings,
  applyBrandingToDocument,
  DEFAULT_BRANDING,
  DEFAULT_LOGIN_PAGE,
} from '../../services/platformSettings';
import { uploadStorageImage } from '../../services/websiteAssets';
import './AdminPages.css';

const ROLE_LABELS_MAP = {
  super_admin: 'Founder',
  admin:       'Administrator',
  hr:          'HR Manager',
  mentor:      'Mentor',
  finance:     'Finance Dept',
  employee:    'Employee',
};

function mapPwError(err) {
  const code = err?.code;
  if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') return 'Current password is incorrect';
  if (code === 'auth/weak-password') return 'Password is too weak';
  if (code === 'auth/requires-recent-login') return 'Please sign out and sign in again, then retry';
  if (code === 'auth/too-many-requests') return 'Too many attempts. Try again later';
  if (code === 'auth/user-not-found') return 'No account with this email in Firebase Auth';
  return err?.message || 'Something went wrong';
}

export default function SettingsPage() {
  const { showToast, data } = useApp();
  const { can, user } = useAuth();
  const canManage = can('canManageSettings');

  const dispatchPlatformSettingsUpdate = useCallback((payload) => {
    try {
      window.dispatchEvent(
        new CustomEvent('fortitude:platformSettingsUpdated', {
          detail: payload,
        })
      );
    } catch {
      // no-op (older browsers / SSR)
    }
  }, []);

  const [tab, setTab] = useState('branding');
  const [showPwModal, setShowPwModal] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [pwForm, setPwForm]     = useState({ old:'', new:'', confirm:'' });
  const [showOldPw, setShowOldPw] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  const [branding, setBranding] = useState({
    primaryColor:   '#000000',
    accentColor:    '#C9A227',
    bgColor:        '#0a0a0a',
    companyName:    'Fortitude IT Solutions',
    tagline:        'Building Digital Excellence',
    logoUrl:        '',
  });

  const [notifSettings, setNotifSettings] = useState({
    newApplicant:   true,
    invoiceOverdue: true,
    teamChatMessage: true,
    contactEnquiry:  true,
    projectUpdate:  true,
    leaveRequest:   true,
    approvalNeeded: true,
    meetingUpdates: true,
    taskUpdates:    true,
    documentUpdates:true,
    emailDigest:    false,
  });

  const [geoSettings, setGeoSettings] = useState({
    enabled:      true,
    lat:          '-26.1076',
    lng:          '28.0567',
    radius:       '200',
    address:      'Sandton, Johannesburg, 2196',
    enforceOnRemote: false,
  });

  const [emailSettings, setEmailSettings] = useState({
    smtpHost:     'smtp.gmail.com',
    smtpPort:     '587',
    senderEmail:  'noreply@fitss.co.za',
    senderName:   'Fortitude IT System',
  });

  const [loginPage, setLoginPage] = useState(() => ({
    ...DEFAULT_LOGIN_PAGE,
    demoAccounts: [...DEFAULT_LOGIN_PAGE.demoAccounts],
  }));

  const [settingsLoading, setSettingsLoading] = useState(false);

  useEffect(() => {
    if (!canManage) return undefined;
    let cancelled = false;
    setSettingsLoading(true);
    (async () => {
      try {
        const s = await fetchPlatformSettings();
        if (cancelled) return;
        setBranding(s.branding);
        setNotifSettings(s.notifSettings);
        setGeoSettings(s.geoSettings);
        setEmailSettings(s.emailSettings);
        setLoginPage({
          ...s.loginPage,
          demoAccounts: [...(s.loginPage?.demoAccounts || DEFAULT_LOGIN_PAGE.demoAccounts)],
        });
        applyBrandingToDocument(s.branding);
      } catch (e) {
        if (!cancelled) showToast(e.message || 'Could not load settings from Firestore', 'error');
      } finally {
        if (!cancelled) setSettingsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [canManage, showToast]);

  const persistAllSettings = useCallback(async () => {
    await savePlatformSettings({
      branding,
      notifSettings,
      geoSettings,
      emailSettings,
      loginPage,
    });

    dispatchPlatformSettingsUpdate({
      branding,
      notifSettings,
      geoSettings,
      emailSettings,
      loginPage,
    });
  }, [branding, notifSettings, geoSettings, emailSettings, loginPage, dispatchPlatformSettingsUpdate]);

  const applyTheme = async () => {
    applyBrandingToDocument(branding);
    try {
      await persistAllSettings();
      showToast('Theme applied and saved to Firestore');
    } catch (e) {
      showToast(e.message || 'Could not save theme', 'error');
    }
  };

  const resetTheme = async () => {
    const b = { ...DEFAULT_BRANDING };
    setBranding(b);
    applyBrandingToDocument(b);
    try {
      await savePlatformSettings({
        branding: b,
        notifSettings,
        geoSettings,
        emailSettings,
        loginPage,
      });
      showToast('Theme reset to defaults and saved');

      dispatchPlatformSettingsUpdate({
        branding: b,
        notifSettings,
        geoSettings,
        emailSettings,
        loginPage,
      });
    } catch (e) {
      showToast(e.message || 'Could not save', 'error');
    }
  };

  const saveNotificationPrefs = async () => {
    try {
      await persistAllSettings();
      showToast('Notification preferences saved');
    } catch (e) {
      showToast(e.message || 'Save failed', 'error');
    }
  };

  const saveGeoPrefs = async () => {
    try {
      await persistAllSettings();
      showToast('Geo-location settings saved');
    } catch (e) {
      showToast(e.message || 'Save failed', 'error');
    }
  };

  const saveEmailPrefs = async () => {
    try {
      await persistAllSettings();
      showToast('Email settings saved — set MAIL_USER and MAIL_PASS on Cloud Functions for delivery');
    } catch (e) {
      showToast(e.message || 'Save failed', 'error');
    }
  };

  const saveLoginScreen = async () => {
    try {
      await persistAllSettings();
      showToast('Login screen saved — updates apply on /login immediately');
    } catch (e) {
      showToast(e.message || 'Save failed', 'error');
    }
  };

  const uploadSystemLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const url = await uploadStorageImage('website/system/logo', file);
      const nextBranding = { ...branding, logoUrl: url };
      setBranding(nextBranding);
      applyBrandingToDocument(nextBranding);
      await savePlatformSettings({
        branding: nextBranding,
        notifSettings,
        geoSettings,
        emailSettings,
        loginPage,
      });
      dispatchPlatformSettingsUpdate({
        branding: nextBranding,
        notifSettings,
        geoSettings,
        emailSettings,
        loginPage,
      });
      showToast('System logo uploaded and applied across the platform');
    } catch (err) {
      showToast(err.message || 'Upload failed', 'error');
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      await sendTestEmail(user?.email || '');
      showToast(`Test email sent to ${user?.email}`);
    } catch (err) {
      showToast(err.message || 'Could not send test email', 'error');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const accountEmployees = (data.employees || []).filter(
    (emp) =>
      emp.email &&
      (emp.role !== 'super_admin' || String(emp.id) === String(user?.employeeId))
  );

  const isSelfPasswordTarget =
    pwTarget?.email &&
    user?.email &&
    pwTarget.email.toLowerCase() === user.email.toLowerCase();

  const savePw = async () => {
    if (!pwTarget?.email) return;
    const auth = getFirebaseAuth();

    if (isSelfPasswordTarget) {
      if (!pwForm.old) {
        showToast('Enter your current password', 'error');
        return;
      }
      if (!pwForm.new || pwForm.new.length < 8) {
        showToast('New password must be at least 8 characters', 'error');
        return;
      }
      if (pwForm.new !== pwForm.confirm) {
        showToast('Passwords do not match', 'error');
        return;
      }
      setPwSaving(true);
      try {
        const cred = EmailAuthProvider.credential(user.email, pwForm.old);
        await reauthenticateWithCredential(auth.currentUser, cred);
        await updatePassword(auth.currentUser, pwForm.new);
        showToast(`Password updated for ${pwTarget.name}`);
        setShowPwModal(false);
        setPwForm({ old: '', new: '', confirm: '' });
        setPwTarget(null);
      } catch (e) {
        showToast(mapPwError(e), 'error');
      } finally {
        setPwSaving(false);
      }
      return;
    }

    setPwSaving(true);
    try {
      await sendPasswordResetEmail(auth, pwTarget.email.trim());
      showToast(`Reset link sent to ${pwTarget.email}`);
      setShowPwModal(false);
      setPwForm({ old: '', new: '', confirm: '' });
      setPwTarget(null);
    } catch (e) {
      showToast(mapPwError(e), 'error');
    } finally {
      setPwSaving(false);
    }
  };

  if (!canManage) {
    return (
      <div className="admin-page">
        <div className="page-header"><div><h1 className="page-title">Settings</h1></div></div>
        <div className="perm-denied">
          <div className="perm-denied-icon"><Lock size={26} style={{ color:'var(--text-dim)' }}/></div>
          <p style={{ fontWeight:700 }}>Founders Only</p>
          <p style={{ color:'var(--text-muted)',fontSize:'0.875rem' }}>System settings can only be modified by Bokang and Thobane.</p>
        </div>
      </div>
    );
  }

  const settingsTabs = [
    { id:'branding',      label:'Branding & Theme',    icon:Palette },
    { id:'notifications', label:'Notifications',       icon:Bell },
    { id:'geolocation',   label:'Geo-location',        icon:MapPin },
    { id:'email',         label:'Email / SMTP',        icon:Mail },
    { id:'accounts',      label:'User Accounts',       icon:Shield },
  ];

  return (
    <div className="admin-page">
      <div className="page-header">
        <div><h1 className="page-title">System Settings</h1><p className="page-subtitle">Founders only — manage all platform configurations</p></div>
      </div>

      {settingsLoading && (
        <p style={{ fontSize:'0.8rem', color:'var(--text-muted)', marginBottom:12 }}>Loading saved settings…</p>
      )}

      <div style={{ display:'flex',gap:20,alignItems:'flex-start' }}>
        {/* Sidebar nav */}
        <div style={{ width:210,flexShrink:0,display:'flex',flexDirection:'column',gap:4 }}>
          {settingsTabs.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{ display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:'var(--radius-md)',background:tab===t.id?'rgba(201,162,39,0.1)':'transparent',border:`1px solid ${tab===t.id?'var(--border-accent)':'transparent'}`,color:tab===t.id?'var(--secondary-color)':'var(--text-muted)',fontWeight:tab===t.id?700:400,fontSize:'0.85rem',cursor:'pointer',transition:'var(--transition)',textAlign:'left',fontFamily:'var(--font-body)' }}>
              <t.icon size={15}/>{t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex:1 }}>

          {/* ── BRANDING ── */}
          {tab==='branding'&&(
            <>
            <div className="card" style={{ maxWidth:580 }}>
              <p className="card-title" style={{ marginBottom:4 }}>Branding &amp; Theme</p>
              <p className="card-subtitle" style={{ marginBottom:22 }}>Changes apply instantly across the entire platform</p>
              <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
                <div className="form-group"><label className="form-label">Company Name</label><input className="form-input" value={branding.companyName} onChange={e=>setBranding({...branding,companyName:e.target.value})}/></div>
                <div className="form-group"><label className="form-label">Tagline</label><input className="form-input" value={branding.tagline} onChange={e=>setBranding({...branding,tagline:e.target.value})}/></div>
                <div className="form-group">
                  <label className="form-label">System Logo</label>
                  <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                    <label className="btn-outline" style={{ display:'inline-flex', alignItems:'center', gap:8, cursor:'pointer', margin:0 }}>
                      <Upload size={14}/> Upload logo
                      <input type="file" accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml" style={{ display:'none' }} onChange={uploadSystemLogo}/>
                    </label>
                    {branding.logoUrl && <img src={branding.logoUrl} alt="" style={{ width:56, height:56, objectFit:'contain', borderRadius:'var(--radius-sm)', border:'1px solid var(--border-color)', background:'var(--surface-2)', padding:6 }} />}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Accent / Gold Color</label>
                    <div style={{ display:'flex',gap:10,alignItems:'center' }}>
                      <input type="color" value={branding.accentColor} onChange={e=>setBranding({...branding,accentColor:e.target.value})} style={{ width:44,height:38,padding:2,border:'1px solid var(--border-color)',borderRadius:'var(--radius-sm)',cursor:'pointer',background:'var(--surface-2)' }}/>
                      <input className="form-input" value={branding.accentColor} onChange={e=>setBranding({...branding,accentColor:e.target.value})} style={{ fontFamily:'var(--font-mono)',fontSize:'0.85rem' }}/>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Background Color</label>
                    <div style={{ display:'flex',gap:10,alignItems:'center' }}>
                      <input type="color" value={branding.bgColor} onChange={e=>setBranding({...branding,bgColor:e.target.value})} style={{ width:44,height:38,padding:2,border:'1px solid var(--border-color)',borderRadius:'var(--radius-sm)',cursor:'pointer',background:'var(--surface-2)' }}/>
                      <input className="form-input" value={branding.bgColor} onChange={e=>setBranding({...branding,bgColor:e.target.value})} style={{ fontFamily:'var(--font-mono)',fontSize:'0.85rem' }}/>
                    </div>
                  </div>
                </div>
                {/* Preview band */}
                <div style={{ height:48,borderRadius:'var(--radius-md)',background:branding.accentColor,display:'flex',alignItems:'center',justifyContent:'center',gap:10 }}>
                  <Zap size={18} color={branding.bgColor}/>
                  <span style={{ fontWeight:800,letterSpacing:'0.1em',color:branding.bgColor,fontSize:'0.9rem' }}>{branding.companyName.toUpperCase()}</span>
                </div>
                <div style={{ display:'flex',gap:10 }}>
                  <button className="btn-gold" style={{ flex:1 }} onClick={applyTheme}><Save size={14}/> Apply Theme</button>
                  <button className="btn-ghost" onClick={resetTheme}>Reset Default</button>
                </div>
              </div>
            </div>

            <div className="card" style={{ maxWidth:580, marginTop:20 }}>
              <p className="card-title" style={{ marginBottom:4 }}>Login screen</p>
              <p className="card-subtitle" style={{ marginBottom:18 }}>Public `/login` page — copy, quick-fill, and footer badge</p>
              <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 0',borderBottom:'1px solid var(--border-color)',marginBottom:14 }}>
                <div>
                  <p style={{ fontWeight:600,fontSize:'0.875rem' }}>Show quick-fill demo accounts</p>
                  <p style={{ fontSize:'0.78rem',color:'var(--text-muted)' }}>Uses `loginPage.demoAccounts` from Firestore (seeded with Auth users). Hide for production if needed.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setLoginPage((lp) => ({ ...lp, showQuickFill: !lp.showQuickFill }))}
                  style={{ width:44,height:24,borderRadius:12,background:loginPage.showQuickFill?'var(--secondary-color)':'var(--surface-3)',border:`1px solid ${loginPage.showQuickFill?'var(--secondary-color)':'var(--border-color)'}`,cursor:'pointer',position:'relative',transition:'all 0.2s' }}
                >
                  <div style={{ width:18,height:18,borderRadius:9,background:'#fff',position:'absolute',top:2,left:loginPage.showQuickFill?22:2,transition:'left 0.2s',boxShadow:'0 1px 3px rgba(0,0,0,0.3)' }} />
                </button>
              </div>
              <div className="form-group"><label className="form-label">Quick-fill hint</label><input className="form-input" value={loginPage.quickFillHint||''} onChange={e=>setLoginPage({...loginPage,quickFillHint:e.target.value})} /></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Card title</label><input className="form-input" value={loginPage.cardTitle||''} onChange={e=>setLoginPage({...loginPage,cardTitle:e.target.value})} /></div>
                <div className="form-group"><label className="form-label">Card subtitle</label><input className="form-input" value={loginPage.cardSubtitle||''} onChange={e=>setLoginPage({...loginPage,cardSubtitle:e.target.value})} /></div>
              </div>
              <div className="form-group"><label className="form-label">Side panel title</label><input className="form-input" value={loginPage.infoTitle||''} onChange={e=>setLoginPage({...loginPage,infoTitle:e.target.value})} /></div>
              <div className="form-group"><label className="form-label">Side panel text</label><textarea className="form-input" rows={3} value={loginPage.infoText||''} onChange={e=>setLoginPage({...loginPage,infoText:e.target.value})} /></div>
              <div className="form-group"><label className="form-label">Footer badge</label><input className="form-input" value={loginPage.footerBadge||''} onChange={e=>setLoginPage({...loginPage,footerBadge:e.target.value})} placeholder="Firebase" /></div>
              <div className="info-banner info-banner-blue" style={{ marginBottom:14 }}><Shield size={14}/><span>To change which emails appear in quick-fill, update `loginPage.demoAccounts` in Firestore or re-run the seed script.</span></div>
              <button className="btn-gold" style={{ width:'100%' }} onClick={saveLoginScreen}><Save size={14}/> Save login screen</button>
            </div>
            </>
          )}

          {/* ── NOTIFICATIONS ── */}
          {tab==='notifications'&&(
            <div className="card" style={{ maxWidth:520 }}>
              <p className="card-title" style={{ marginBottom:4 }}>Notification Preferences</p>
              <p className="card-subtitle" style={{ marginBottom:22 }}>Each toggle controls in-app notifications and matching Cloud Function emails (when MAIL_USER / MAIL_PASS are set)</p>
              <div style={{ display:'flex',flexDirection:'column',gap:4 }}>
                {Object.entries(notifSettings).filter(([key]) => key !== 'newMessage').map(([key,val])=>{
                  const labels = {
                    newApplicant:   'New job applicant received',
                    invoiceOverdue: 'Invoice overdue (auto-mark 06:30, alerts 07:00)',
                    teamChatMessage: 'Team chat message',
                    contactEnquiry:  'Website contact form enquiry',
                    projectUpdate:  'Project status changed',
                    leaveRequest:   'Employee leave request',
                    approvalNeeded: 'Approvals (founders + requester + partial sign-off)',
                    meetingUpdates: 'Meetings (invite, update, cancel)',
                    taskUpdates:    'Tasks (assigned + status changes)',
                    documentUpdates:'Documents (approval + issuance)',
                    emailDigest:    'Daily email digest (06:00)',
                  };
                  return (
                    <div key={key} style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 0',borderBottom:'1px solid var(--border-color)' }}>
                      <span style={{ fontSize:'0.875rem' }}>{labels[key]}</span>
                      <button onClick={()=>setNotifSettings(prev=>({...prev,[key]:!prev[key]}))}
                        style={{ width:44,height:24,borderRadius:12,background:val?'var(--secondary-color)':'var(--surface-3)',border:`1px solid ${val?'var(--secondary-color)':'var(--border-color)'}`,cursor:'pointer',position:'relative',transition:'all 0.2s' }}>
                        <div style={{ width:18,height:18,borderRadius:9,background:'#fff',position:'absolute',top:2,left:val?22:2,transition:'left 0.2s',boxShadow:'0 1px 3px rgba(0,0,0,0.3)' }}/>
                      </button>
                    </div>
                  );
                })}
              </div>
              <button className="btn-gold" style={{ marginTop:18,width:'100%' }} onClick={saveNotificationPrefs}><Save size={14}/> Save Preferences</button>
            </div>
          )}

          {/* ── GEO-LOCATION ── */}
          {tab==='geolocation'&&(
            <div className="card" style={{ maxWidth:540 }}>
              <p className="card-title" style={{ marginBottom:4 }}>Geo-Location Settings</p>
              <p className="card-subtitle" style={{ marginBottom:22 }}>Define the office perimeter for attendance tracking</p>
              <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
                <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 0',borderBottom:'1px solid var(--border-color)' }}>
                  <div><p style={{ fontWeight:600,fontSize:'0.875rem' }}>Enable Geo-verification</p><p style={{ fontSize:'0.78rem',color:'var(--text-muted)' }}>Require employees to be within perimeter to check in</p></div>
                  <button onClick={()=>setGeoSettings(g=>({...g,enabled:!g.enabled}))}
                    style={{ width:44,height:24,borderRadius:12,background:geoSettings.enabled?'var(--secondary-color)':'var(--surface-3)',border:'none',cursor:'pointer',position:'relative',transition:'all 0.2s' }}>
                    <div style={{ width:18,height:18,borderRadius:9,background:'#fff',position:'absolute',top:2,left:geoSettings.enabled?22:2,transition:'left 0.2s' }}/>
                  </button>
                </div>
                <div className="form-group"><label className="form-label">Office Address</label><input className="form-input" value={geoSettings.address} onChange={e=>setGeoSettings(g=>({...g,address:e.target.value}))}/></div>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">Latitude</label><input className="form-input" value={geoSettings.lat} onChange={e=>setGeoSettings(g=>({...g,lat:e.target.value}))}/></div>
                  <div className="form-group"><label className="form-label">Longitude</label><input className="form-input" value={geoSettings.lng} onChange={e=>setGeoSettings(g=>({...g,lng:e.target.value}))}/></div>
                </div>
                <div className="form-group"><label className="form-label">Radius (metres)</label><input className="form-input" type="number" value={geoSettings.radius} onChange={e=>setGeoSettings(g=>({...g,radius:e.target.value}))}/></div>
                <div className="info-banner info-banner-blue"><MapPin size={14}/><span>Employees outside {geoSettings.radius}m of the office will be marked as Remote.</span></div>
                <button className="btn-gold" onClick={saveGeoPrefs}><Save size={14}/> Save Settings</button>
              </div>
            </div>
          )}

          {/* ── EMAIL / SMTP ── */}
          {tab==='email'&&(
            <div className="card" style={{ maxWidth:520 }}>
              <p className="card-title" style={{ marginBottom:4 }}>Email / SMTP Settings</p>
              <p className="card-subtitle" style={{ marginBottom:22 }}>Configure outgoing email for notifications and documents</p>
              <div className="info-banner info-banner-gold" style={{ marginBottom:16 }}>
                <Mail size={14}/>
                <span>
                  Put MAIL_USER and MAIL_PASS in the project root `.env`, then run `npm run sync:mail-env` and `firebase deploy --only functions` (or `npm run deploy:functions`). Do not use deprecated `firebase functions:config:set`. Host and sender below are saved to Firestore.
                </span>
              </div>
              <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">SMTP Host</label><input className="form-input" value={emailSettings.smtpHost} onChange={e=>setEmailSettings(s=>({...s,smtpHost:e.target.value}))}/></div>
                  <div className="form-group"><label className="form-label">SMTP Port</label><input className="form-input" type="number" value={emailSettings.smtpPort} onChange={e=>setEmailSettings(s=>({...s,smtpPort:e.target.value}))}/></div>
                </div>
                <div className="form-group"><label className="form-label">Sender Email</label><input className="form-input" type="email" value={emailSettings.senderEmail} onChange={e=>setEmailSettings(s=>({...s,senderEmail:e.target.value}))}/></div>
                <div className="form-group"><label className="form-label">Sender Display Name</label><input className="form-input" value={emailSettings.senderName} onChange={e=>setEmailSettings(s=>({...s,senderName:e.target.value}))}/></div>
                <div style={{ display:'flex',gap:10 }}>
                  <button className="btn-ghost" onClick={handleSendTestEmail} disabled={sendingTestEmail}>{sendingTestEmail ? 'Sending…' : 'Send Test Email'}</button>
                  <button className="btn-gold" style={{ flex:1 }} onClick={saveEmailPrefs}><Save size={14}/> Save Settings</button>
                </div>
              </div>
            </div>
          )}

          {/* ── USER ACCOUNTS ── */}
          {tab==='accounts'&&(
            <div className="card">
              <p className="card-title" style={{ marginBottom:4 }}>User Accounts &amp; Passwords</p>
              <p className="card-subtitle" style={{ marginBottom:22 }}>Reset passwords and manage login access for all users</p>
              <div style={{ display:'flex',flexDirection:'column',gap:8 }}>
                {accountEmployees.map((emp) => {
                  const initials = emp.avatar || emp.name?.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase() || '?';
                  const color = emp.role === 'super_admin' ? '#C9A227' : '#3182ce';
                  return (
                  <div key={emp.id} style={{ display:'flex',alignItems:'center',gap:14,padding:'12px 14px',background:'var(--surface-2)',borderRadius:'var(--radius-md)',border:'1px solid var(--border-color)' }}>
                    <div className="av av-sm" style={{ background:`linear-gradient(135deg,${color},${color}88)`,color:'#000',fontWeight:900 }}>{initials}</div>
                    <div style={{ flex:1 }}>
                      <p style={{ fontSize:'0.875rem',fontWeight:700 }}>{emp.name}</p>
                      <p style={{ fontSize:'0.73rem',color:'var(--text-muted)' }}>{ROLE_LABELS_MAP[emp.role]} · {emp.email}</p>
                    </div>
                    <button className="btn-ghost" style={{ display:'flex',alignItems:'center',gap:6,fontSize:'0.75rem' }}
                      onClick={()=>{setPwTarget(emp);setShowPwModal(true);setPwForm({old:'',new:'',confirm:''});}}>
                      <Key size={12}/> {user?.email?.toLowerCase() === emp.email?.toLowerCase() ? 'Change password' : 'Reset access'}
                    </button>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── PASSWORD MODAL ── */}
      {showPwModal&&(
        <div className="modal-overlay" onClick={()=>setShowPwModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div><p className="modal-title">Reset Password</p><p className="modal-subtitle">{pwTarget?.name}</p></div>
              <button className="modal-close" onClick={()=>setShowPwModal(false)}><X size={18}/></button>
            </div>
            <div className="modal-form">
              {isSelfPasswordTarget ? (
                <>
                  <div className="form-group" style={{ position:'relative' }}>
                    <label className="form-label">Current Password</label>
                    <input className="form-input" type={showOldPw?'text':'password'} placeholder="••••••••" value={pwForm.old} onChange={e=>setPwForm({...pwForm,old:e.target.value})} style={{ paddingRight:40 }}/>
                    <button type="button" style={{ position:'absolute',right:12,bottom:9,background:'none',border:'none',color:'var(--text-dim)',cursor:'pointer' }} onClick={()=>setShowOldPw(!showOldPw)}>{showOldPw?<EyeOff size={15}/>:<Eye size={15}/>}</button>
                  </div>
                  <div className="form-group"><label className="form-label">New Password</label><input className="form-input" type="password" placeholder="Min. 8 characters" value={pwForm.new} onChange={e=>setPwForm({...pwForm,new:e.target.value})}/></div>
                  <div className="form-group"><label className="form-label">Confirm New Password</label><input className="form-input" type="password" placeholder="Repeat new password" value={pwForm.confirm} onChange={e=>setPwForm({...pwForm,confirm:e.target.value})}/></div>
                  <div className="info-banner info-banner-gold"><Shield size={14}/><span>Re-enter your current password so Firebase can confirm it is you.</span></div>
                </>
              ) : (
                <>
                  <p style={{ fontSize:'0.875rem', color:'var(--text-muted)', lineHeight:1.6, marginBottom:12 }}>
                    Firebase will email a password reset link to <strong style={{ color:'var(--text-primary)' }}>{pwTarget?.email}</strong>. They set a new password from that link.
                  </p>
                  <div className="info-banner info-banner-blue"><Mail size={14}/><span>The account must exist in Firebase Authentication (e.g. after running the seed script).</span></div>
                </>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" disabled={pwSaving} onClick={()=>setShowPwModal(false)}>Cancel</button>
              <button className="btn-gold" disabled={pwSaving} onClick={savePw}>
                <Key size={14}/> {isSelfPasswordTarget ? (pwSaving ? 'Updating…' : 'Update Password') : (pwSaving ? 'Sending…' : 'Send reset email')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
